import { useCallback, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  GripVertical,
  Star,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  ImageIcon,
  Expand,
} from 'lucide-react'
import {
  usePropertyImages,
  useUpdateImageOrder,
  useDeleteImage,
  useReorderImages,
  type PropertyImage,
} from '@/hooks'
import { ImageLightbox } from '@/components/ui/ImageLightbox'

// ============================================================================
// Props Interface
// ============================================================================

interface ImageGalleryProps {
  /** Property ID to manage images for */
  propertyId: string
  /** Whether the gallery is disabled */
  disabled?: boolean
}

// ============================================================================
// Main Component
// ============================================================================

export function ImageGallery({ propertyId, disabled = false }: ImageGalleryProps) {
  const [error, setError] = useState<string | null>(null)
  const [localImages, setLocalImages] = useState<PropertyImage[] | null>(null)
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  // Queries and mutations
  const { data: images = [], isLoading: isLoadingImages } = usePropertyImages(propertyId)
  const updateOrderMutation = useUpdateImageOrder()
  const deleteMutation = useDeleteImage()
  const reorderMutation = useReorderImages()

  // Use local images if we have them (during drag), otherwise use server data
  const displayImages = localImages ?? images

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px drag before starting
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end - reorder images
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        setLocalImages(null)
        return
      }

      const oldIndex = displayImages.findIndex((img: PropertyImage) => img.id === active.id)
      const newIndex = displayImages.findIndex((img: PropertyImage) => img.id === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        setLocalImages(null)
        return
      }

      // Optimistically update local state
      const newOrder = arrayMove(displayImages, oldIndex, newIndex) as PropertyImage[]
      setLocalImages(newOrder)

      // Prepare order items for API
      const orderItems = newOrder.map((img: PropertyImage, index: number) => ({
        id: img.id,
        sort_order: index,
      }))

      try {
        await updateOrderMutation.mutateAsync({
          items: orderItems,
          propertyId,
        })
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al actualizar el orden de imágenes')
        // Revert to server state
        setLocalImages(null)
      } finally {
        // Clear local state after successful update
        setLocalImages(null)
      }
    },
    [displayImages, propertyId, updateOrderMutation]
  )

  // Handle delete with confirmation
  const handleDelete = useCallback(
    async (image: PropertyImage) => {
      if (!confirm(`¿Eliminar "${image.alt_text || 'esta imagen'}"? Esta acción no se puede deshacer.`)) {
        return
      }

      try {
        await deleteMutation.mutateAsync({
          id: image.id,
          propertyId,
        })
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al eliminar imagen')
      }
    },
    [deleteMutation, propertyId]
  )

  // Handle set primary - moves the image to position 0
  const handleSetPrimary = useCallback(
    async (image: PropertyImage) => {
      if (image.is_primary) return

      setSettingPrimaryId(image.id)
      try {
        // Move this image to first position, keep others in order
        const otherImages = displayImages.filter((img: PropertyImage) => img.id !== image.id)
        const newOrder = [image.id, ...otherImages.map((img: PropertyImage) => img.id)]

        await reorderMutation.mutateAsync({
          propertyId,
          imageIds: newOrder,
        })
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al establecer imagen principal')
      } finally {
        setSettingPrimaryId(null)
      }
    },
    [reorderMutation, propertyId, displayImages]
  )

  const isBusy =
    updateOrderMutation.isPending ||
    deleteMutation.isPending ||
    settingPrimaryId !== null

  // Handle opening image in lightbox
  const handleImageClick = useCallback((index: number) => {
    setLightboxIndex(index)
  }, [])

  // Estado de carga
  if (isLoadingImages) {
    return (
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Galería de Imágenes</h2>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </section>
    )
  }

  // Estado vacío
  if (displayImages.length === 0) {
    return (
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Galería de Imágenes</h2>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ImageIcon className="h-16 w-16 text-muted-foreground/30" />
          <p className="mt-4 text-muted-foreground">Aún no hay imágenes subidas</p>
          <p className="mt-1 text-sm text-muted-foreground/70">
            Usa el cargador de arriba para añadir imágenes
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Galería de Imágenes</h2>
        <p className="text-sm text-muted-foreground">
          {displayImages.length} imagen{displayImages.length !== 1 ? 'es' : ''} • Arrastra para reordenar
        </p>
      </div>

      {/* Mostrar Error */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="flex-shrink-0 p-1 hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Sortable Image Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={displayImages.map((img: PropertyImage) => img.id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {displayImages.map((image: PropertyImage, index: number) => (
              <SortableImageCard
                key={image.id}
                image={image}
                onDelete={() => handleDelete(image)}
                onSetPrimary={() => handleSetPrimary(image)}
                onImageClick={() => handleImageClick(index)}
                isDeleting={
                  deleteMutation.isPending &&
                  deleteMutation.variables?.id === image.id
                }
                isSettingPrimary={settingPrimaryId === image.id}
                disabled={disabled || isBusy}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Fullscreen Image Lightbox */}
      <ImageLightbox
        images={displayImages}
        currentIndex={lightboxIndex ?? 0}
        isOpen={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </section>
  )
}

// ============================================================================
// Sortable Image Card Component
// ============================================================================

interface SortableImageCardProps {
  image: PropertyImage
  onDelete: () => void
  onSetPrimary: () => void
  onImageClick: () => void
  isDeleting: boolean
  isSettingPrimary: boolean
  disabled: boolean
}

function SortableImageCard({
  image,
  onDelete,
  onSetPrimary,
  onImageClick,
  isDeleting,
  isSettingPrimary,
  disabled,
}: SortableImageCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        relative overflow-hidden rounded-lg border bg-background shadow-sm
        ${isDragging ? 'ring-2 ring-primary shadow-lg' : ''}
        ${disabled ? 'opacity-50' : ''}
      `}
    >
      {/* Asa de Arrastre */}
      <button
        type="button"
        className={`
          absolute left-2 top-2 z-10 rounded bg-black/60 p-1.5 text-white
          transition-opacity hover:bg-black/80
          ${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}
        `}
        {...attributes}
        {...listeners}
        disabled={disabled}
        title="Arrastra para reordenar"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Insignia Principal */}
      {image.is_primary && (
        <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
          <Star className="h-3 w-3 fill-current" />
          Principal
        </div>
      )}

      {/* Image Preview - Clickable for fullscreen */}
      <button
        type="button"
        onClick={onImageClick}
        className="group/img relative aspect-video w-full bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Ver imagen en pantalla completa"
      >
        <img
          src={image.url}
          alt={image.alt_text || 'Property image'}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {/* Hover overlay with expand icon */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-200 group-hover/img:opacity-100">
          <Expand className="h-8 w-8 text-white drop-shadow-lg" />
        </div>
      </button>

      {/* Información y Acciones de Imagen */}
      <div className="p-3">
        {/* Texto alternativo (solo lectura) */}
        <div className="mb-3">
          <p className="truncate text-sm text-muted-foreground">
            {image.alt_text || <span className="italic">Imagen de propiedad</span>}
          </p>
        </div>

        {/* Botones de Acción */}
        <div className="flex gap-2">
          {/* Botón Establecer Principal */}
          {!image.is_primary && (
            <button
              type="button"
              onClick={onSetPrimary}
              disabled={disabled || isSettingPrimary}
              className="flex flex-1 items-center justify-center gap-1 rounded border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
            >
              {isSettingPrimary ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Star className="h-3 w-3" />
              )}
              Establecer Principal
            </button>
          )}

          {/* Botón Eliminar */}
          <button
            type="button"
            onClick={onDelete}
            disabled={disabled || isDeleting}
            className="flex items-center justify-center gap-1 rounded border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            {isDeleting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  )
}
